export function normalizeChainId(value) {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }

  if (typeof value !== "string" || value.trim() === "") return null;
  const normalized = value.trim();
  const chainId = /^0x[0-9a-f]+$/i.test(normalized)
    ? Number.parseInt(normalized.slice(2), 16)
    : Number(normalized);
  return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
}

export function networkForChainId(chainId, networks) {
  const normalized = normalizeChainId(chainId);
  if (normalized === null) return null;
  return Object.values(networks || {}).find(
    (candidate) => normalizeChainId(candidate?.Chainid) === normalized,
  ) || null;
}

export function nextAdminNetwork(chainId, networks) {
  const current = networkForChainId(chainId, networks);
  if (current === networks?.ETH) return networks.BSC;
  if (current === networks?.BSC) return networks.ETH;
  return networks?.BSC || networks?.ETH || null;
}

export async function readProviderChainId(provider) {
  if (!provider || typeof provider.request !== "function") {
    throw new Error("The connected wallet does not support network switching");
  }
  const chainId = normalizeChainId(await provider.request({ method: "eth_chainId" }));
  if (chainId === null) throw new Error("The wallet returned an invalid chain ID");
  return chainId;
}

function providerErrorCode(error) {
  return error?.code
    ?? error?.data?.originalError?.code
    ?? error?.data?.code;
}

function addChainParameters(target) {
  const rpcUrls = Array.isArray(target?.rpcUrls)
    ? target.rpcUrls.filter(Boolean)
    : [target?.BNBPROVIDER].filter(Boolean);
  if (!rpcUrls.length) {
    throw new Error(`${target?.name || "The target network"} has no configured RPC URL`);
  }

  return {
    chainId: target.chainId,
    chainName: target.chainName || target.name,
    nativeCurrency: target.nativeCurrency || {
      name: target.currencySymbol,
      symbol: target.currencySymbol,
      decimals: 18,
    },
    rpcUrls,
    ...(target.blockExplorerUrls?.length
      ? { blockExplorerUrls: target.blockExplorerUrls }
      : {}),
  };
}

export async function switchProviderNetwork(provider, target) {
  if (!target?.chainId) throw new Error("The target network is not configured");
  if (!provider || typeof provider.request !== "function") {
    throw new Error("The connected wallet does not support network switching");
  }

  const switchRequest = {
    method: "wallet_switchEthereumChain",
    params: [{ chainId: target.chainId }],
  };

  try {
    await provider.request(switchRequest);
  } catch (error) {
    if (providerErrorCode(error) !== 4902) throw error;
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [addChainParameters(target)],
    });
    await provider.request(switchRequest);
  }

  const actualChainId = await readProviderChainId(provider);
  if (actualChainId !== normalizeChainId(target.Chainid)) {
    throw new Error(`The wallet did not switch to ${target.name}`);
  }
  return actualChainId;
}
